import { useCallback, useState, useEffect, useMemo } from 'react';
import '@glideapps/glide-data-grid/dist/index.css';
import {
  CompactSelection,
  CustomCell,
  DataEditor,
  GridCell,
  GridCellKind,
  GridColumn,
  GridSelection,
  Item,
  SpriteMap
} from '@glideapps/glide-data-grid';
import { useLayer } from 'react-laag';
import {
  useAddRow,
  useDeleteColumn,
  useDeleteRow,
  useExecuteWorkflow,
  useGetColumns,
  useGetRows,
  useUpdateCell,
  useUpdateColumn,
  useUpdateRow
} from 'apis/business/agent/worksheet/hook';
import { WorksheetResponse } from 'apis/business/agent/worksheet/types';
import { EmptyStateView } from 'components/Common/EmptyStateView';
import {
  Box,
  debounce,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Typography,
  useTheme
} from '@mui/material';
import { RiDeleteBin6Line } from 'react-icons/ri';
import Button from 'components/Common/Button';
import { formatDate } from 'utils/helperFunctions';
import Input from 'components/Common/Input';
import { useForm } from 'react-hook-form';
import TextIcon from 'assets/icons/TextIcon';
import HideColumn from 'assets/icons/HideColumn';

interface DummyItem {
  row_id?: number;
  [key: string]: string | number | undefined;
}

interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
}

export default function WorksheetTable({
  worksheetData,
  filters,
  sorting,
  handleFreezeColumnChange,
  selectedHeight
}: {
  worksheetData: WorksheetResponse | undefined;
  filters: any;
  sorting: any;
  handleFreezeColumnChange: number;
  selectedHeight: number;
}) {
  const theme = useTheme();
  const [data, setData] = useState<DummyItem[]>([]);
  const [currentColumns, setCurrentColumns] = useState<any[]>([]); // State for sortable columns
  const [menu, setMenu] = useState<{ col: number; bounds: Rectangle }>();
  const [columnID, setColumnID] = useState<string>();
  const [columnName, setColumnName] = useState<string | null>(null);
  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty()
  });

  const { control, handleSubmit } = useForm({});
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const { data: columnsData } = useGetColumns();
  const { data: rowsData } = useGetRows(filters, sorting);

  const { mutateAsync: handleAddRow } = useAddRow();
  const { mutateAsync: handleUpdateCell } = useUpdateCell();
  const { mutateAsync: handleUpdateColumn } = useUpdateColumn();
  const { mutateAsync: handleUpdateRow } = useUpdateRow();
  const { mutateAsync: handleDeleteColumn } = useDeleteColumn();
  const { mutateAsync: handleDeleteRow } = useDeleteRow();
  const { mutateAsync: handleExecuteWorkflow } = useExecuteWorkflow();

  const isOpen = menu !== undefined;
  const { layerProps, renderLayer } = useLayer({
    isOpen,
    auto: true,
    placement: 'bottom-end',
    triggerOffset: 2,
    onOutsideClick: () => setMenu(undefined),
    trigger: {
      getBounds: () => ({
        left: menu?.bounds.x ?? 0,
        top: menu?.bounds.y ?? 0,
        width: menu?.bounds.width ?? 0,
        height: menu?.bounds.height ?? 0,
        right: (menu?.bounds.x ?? 0) + (menu?.bounds.width ?? 0),
        bottom: (menu?.bounds.y ?? 0) + (menu?.bounds.height ?? 0)
      })
    }
  });

  const customHeaderIcons: SpriteMap = {
    customTextIcon: () => `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <mask id="mask0_7281_8663" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="20" height="20">
          <rect width="20" height="20" fill="#D9D9D9"/>
        </mask>
        <g mask="url(#mask0_7281_8663)">
          <path d="M6.16667 16.5963V5.76292H2V4H12.0963V5.76292H7.92958V16.5963H6.16667ZM13.6667 16.5963V9.92958H11.1667V8.16667H17.9296V9.92958H15.4296V16.5963H13.6667Z" fill="#4B22A8"/>
        </g>
      </svg>
    `,
    customDateTimeIcon: () => `
      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 28 26" fill="white">
        <circle cx="12" cy="12" r="10" stroke="#4B22A8" stroke-width="2" fill="white"/>
        <path d="M12 6v6l4 2" stroke="#4B22A8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `,
    customWorkflowIcon: () => `
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <g clip-path="url(#clip0_7274_15853)">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M11.1791 17.2208H7.5297C7.11448 17.2208 7.11448 16.598 7.5297 16.598H11.3083L11.6543 14.9186H1.60122C1.1906 14.9186 1.1906 14.2912 1.60122 14.2912H11.7835L12.1249 12.6164H8.60006C8.18945 12.6164 8.18945 11.989 8.60006 11.989H11.645L9.74885 10.3096H0.350927C-0.0596841 10.3096 -0.0596841 9.68677 0.350927 9.68677H9.07988C8.8492 9.34998 8.88149 8.884 9.15831 8.5795L9.67503 8.00741H7.5297C7.11448 8.00741 7.11448 7.37996 7.5297 7.37996H10.2379L11.7558 5.70522H1.60122C1.1906 5.70522 1.1906 5.07777 1.60122 5.07777H12.3186L13.8365 3.39842H9.28749C8.87226 3.39842 8.87226 2.77558 9.28749 2.77558H14.404L16.2587 0.71791C16.8677 0.0443231 17.9841 0.611797 17.7996 1.50222L16.4801 7.74444L19.6404 10.3834C20.0372 10.7156 20.0695 11.3108 19.7142 11.6891L12.4432 19.2969C11.8204 19.9428 10.7408 19.3754 10.9207 18.4988L11.1791 17.2208ZM16.7246 1.13775L9.61967 8.99934C9.51817 9.11007 9.53201 9.28077 9.64274 9.37766L12.7108 12.0997C12.7938 12.1735 12.8354 12.2842 12.8123 12.395L11.5343 18.6233C11.479 18.8863 11.8065 19.057 11.9911 18.8632L19.2621 11.2554C19.3728 11.1401 19.3636 10.9647 19.2437 10.8586L15.9357 8.1043C15.848 8.03048 15.8065 7.91053 15.8296 7.7998L17.186 1.37304C17.2414 1.10545 16.9092 0.93475 16.7246 1.13775ZM7.1883 3.39842H4.55854C4.14793 3.39842 4.14793 2.77558 4.55854 2.77558H7.1883C7.59891 2.77558 7.59891 3.39842 7.1883 3.39842ZM5.91494 8.00741H4.55854C4.14793 8.00741 4.14793 7.37996 4.55854 7.37996H5.91494C6.33017 7.37996 6.33017 8.00741 5.91494 8.00741ZM6.74539 12.6164H4.55854C4.14793 12.6164 4.14793 11.989 4.55854 11.989H6.74539C7.156 11.989 7.156 12.6164 6.74539 12.6164ZM6.15946 17.2208H4.55854C4.14793 17.2208 4.14793 16.598 4.55854 16.598H6.15946C6.57007 16.598 6.57007 17.2208 6.15946 17.2208Z" fill="#4B22A8"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.6425 9.37609L12.7106 12.0981C12.7936 12.1719 12.8351 12.2827 12.8121 12.3934L11.5341 18.6218C11.4787 18.8847 11.8063 19.0554 11.9908 18.8617L19.2619 11.2538C19.3726 11.1385 19.3634 10.9632 19.2434 10.8571L15.9355 8.10274C15.8478 8.02892 15.8063 7.90896 15.8294 7.79824L17.1858 1.37148C17.2411 1.10389 16.9089 0.933185 16.7244 1.13618L9.61943 8.99778C9.51793 9.1085 9.53177 9.27921 9.6425 9.37609Z" fill="#EAE6FD"/>
      </g>
      <defs>
        <clipPath id="clip0_7274_15853">
          <rect width="20" height="20" fill="white"/>
        </clipPath>
      </defs>
    </svg>
    `
  };

  useEffect(() => {
    if (rowsData?.data?.length && columnsData?.data?.length) {
      const extractedColumns: GridColumn[] | any = columnsData.data.map((col: any) => ({
        title: col.name,
        id: col.id.toString(),
        width: col?.configuration?.width ? col?.configuration?.width : 200,
        hasMenu: !col.is_default,
        is_default: col.is_default,
        data_type: col.data_type,
        workflow_execution_log: col.cells?.map((item: any) => item.workflow_execution_log),
        icon: col.data_type === 'app_execution' ? 'customWorkflowIcon' : 'customTextIcon'
      }));
      extractedColumns.unshift({
        title: 'DATE & TIME',
        id: 'created_at',
        width: 200,
        is_default: true,
        data_type: 'text',
        icon: 'customDateTimeIcon'
      });
      setCurrentColumns(extractedColumns);

      const extractedData: DummyItem[] = rowsData?.data?.reduce((acc: DummyItem[], row: any) => {
        const rowObject: DummyItem = {
          row_id: row.id,
          created_at: formatDate(row.created_at)
        };
        columnsData.data.forEach((col: any) => {
          const cell = row.cells.find((c: any) => c.column_id === col.id);
          rowObject[col.id.toString()] = cell ? cell.value || '' : '';
        });

        acc.push(rowObject);
        return acc;
      }, []);

      setData(extractedData);
    }
  }, [columnsData, rowsData?.data]);

  const getCellContent = useCallback(
    (cell: Item): GridCell => {
      const [col, row] = cell;
      const columnData = currentColumns[col];
      const colId = currentColumns[col]?.id;
      const dataRow = data[row];

      if (columnData?.data_type === 'app_execution') {
        return {
          kind: GridCellKind.Custom,
          allowOverlay: true,
          readonly: true,
          copyData: colId,
          data: { rowId: dataRow?.row_id!, columnId: colId, workFlowLog: columnData?.workflow_execution_log }
        };
      }

      if (!colId || !dataRow) {
        return {
          kind: GridCellKind.Text,
          allowOverlay: true,
          readonly: false,
          displayData: '',
          data: '',
          contentAlign: 'center'
        };
      }

      const cellData = String(dataRow[colId] || '');
      return {
        kind: GridCellKind.Text,
        allowOverlay: true,
        readonly: false,
        displayData: cellData,
        data: cellData
      };
    },
    [data, currentColumns]
  );

  const workflowButtonRenderer = {
    kind: GridCellKind.Custom,
    isMatch: (cell2: CustomCell<{}>): cell2 is CustomCell<any> => {
      return cell2.kind === GridCellKind.Custom;
    },
    draw: (args: any) => {
      const { cell, ctx, rect } = args;
      const { x, y, width, height } = rect;

      if (cell.data.workFlowLog[args.row]) {
        const statusText = cell.data.workFlowLog[args.row]?.status;

        let backgroundColor = '#d4edda';
        let textColor = '#155724';

        if (statusText?.toLowerCase() === 'failed') {
          backgroundColor = '#f8d7da';
          textColor = '#721c24';
        }

        if (statusText?.toLowerCase() === 'in_progress') {
          backgroundColor = '#FFD8A8';
          textColor = '#B86E00';
        }

        const badgePadding = 10;
        const badgeHeight = 20;
        const badgeWidth = ctx.measureText(statusText).width + 2 * badgePadding;
        const badgeX = x + (width - badgeWidth) / 3;
        const badgeY = y + (height - badgeHeight) / 2;

        ctx.fillStyle = backgroundColor;
        ctx.beginPath();
        const radius = badgeHeight / 2;
        ctx.moveTo(badgeX + radius, badgeY);
        ctx.arcTo(badgeX + badgeWidth, badgeY, badgeX + badgeWidth, badgeY + badgeHeight, radius);
        ctx.arcTo(badgeX + badgeWidth, badgeY + badgeHeight, badgeX, badgeY + badgeHeight, radius);
        ctx.arcTo(badgeX, badgeY + badgeHeight, badgeX, badgeY, radius);
        ctx.arcTo(badgeX, badgeY, badgeX + badgeWidth, badgeY, radius);
        ctx.closePath();
        ctx.fill();

        ctx.fillStyle = textColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(statusText.replace(/_/g, ' '), badgeX + badgeWidth / 2, badgeY + badgeHeight / 2);

        if (statusText?.toLowerCase() === 'failed') {
          const buttonWidth = 50;
          const buttonHeight = 20;
          const buttonX = badgeX + badgeWidth + 10;
          const buttonY = badgeY;

          ctx.fillStyle = '#00860D';
          ctx.fillRect(buttonX, buttonY, buttonWidth, buttonHeight);

          ctx.fillStyle = 'white';
          ctx.fillText('Retry', buttonX + buttonWidth / 2, buttonY + buttonHeight / 2);

          cell.data.buttonRect = { x: buttonX, y: buttonY, width: buttonWidth, height: buttonHeight };
        }
        return true;
      } else {
        const centerX = x + width / 2;
        const centerY = y + height / 2;
        const radius = Math.min(width, height) / 2 - 5;
        ctx.fillStyle = theme.palette.primary.main;
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.fillStyle = 'white';
        ctx.beginPath();
        ctx.moveTo(centerX - radius / 3, centerY - radius / 3);
        ctx.lineTo(centerX - radius / 3, centerY + radius / 3);
        ctx.lineTo(centerX + radius / 2, centerY);
        ctx.closePath();
        ctx.fill();
        return true;
      }
    },
    onClick: (args: any) => {
      const columnId = args.cell.data.columnId;
      const rowId = args.cell.data.rowId;
      handleExecuteWorkflow({ limit: 'single', column_id: Number(columnId), row_id: rowId });
    }
  };
  const onHeaderMenuClick = useCallback(
    (col: number, bounds: Rectangle) => {
      setMenu({ col, bounds });
      const columnId = currentColumns[col]?.id;
      setColumnID(columnId);
      setColumnName(currentColumns[col]?.title);
    },
    [currentColumns]
  );

  const onRowAppended = useCallback(() => {
    const newPosition = (worksheetData?.rows?.length || 0) + 1;
    handleAddRow({ row: { position: newPosition } });
  }, [handleAddRow, worksheetData]);

  const onCellEdited = useCallback(
    async (cell: Item, newValue: GridCell) => {
      const [colIndex, rowIndex] = cell;
      if (colIndex === 0) return;
      if (newValue.kind !== GridCellKind.Text) return;

      const rowId = currentColumns[colIndex]?.id; // Use currentColumns here
      const dataRow = data[rowIndex];
      const columnId = dataRow?.row_id;
      if (!columnId || !rowId) return;
      setData((prevData) => {
        const updatedData = [...prevData];
        const row = updatedData[rowIndex];
        if (row) {
          row[rowId] = newValue.data;
        }
        return updatedData;
      });

      handleUpdateCell({
        rowId,
        columnId,
        payload: {
          cell: {
            value: newValue.data
          }
        }
      });
    },
    [currentColumns, data, handleUpdateCell]
  );

  const onColMoved = useCallback(
    (startIndex: number, endIndex: number): void => {
      // Check if the column being moved is default
      if (startIndex === 0) return;
      if (currentColumns[startIndex]?.is_default) {
        console.warn('Cannot move default columns');
        return;
      }

      const columnId = currentColumns[startIndex].id!;
      handleUpdateColumn({
        columnId,
        payload: {
          column: {
            position: endIndex
          }
        }
      });
      setCurrentColumns((old) => {
        const newCols = [...old];
        const [toMove] = newCols.splice(startIndex, 1);
        newCols.splice(endIndex, 0, toMove);
        return newCols;
      });
    },
    [currentColumns, handleUpdateColumn]
  );

  const onRowMoved = useCallback(
    (startIndex: number, endIndex: number): void => {
      if (startIndex === endIndex) return;
      const rowId = String(data[startIndex]?.row_id!);
      handleUpdateRow({
        rowId,
        payload: {
          row: {
            position: endIndex + 1
          }
        }
      });
      const movedRow = data[startIndex];

      const updatedData = [...data];
      updatedData.splice(startIndex, 1);

      updatedData.splice(endIndex, 0, movedRow);

      setData(updatedData);
    },
    [data, setData, handleUpdateRow]
  );

  const onColProposeMove = useCallback((_startIndex: number, endIndex: number): boolean => {
    return endIndex !== 1;
  }, []);

  // For delay hitting column resize api
  const debouncedUpdateColumn = useMemo(
    () =>
      debounce((columnId, newSize) => {
        handleUpdateColumn({
          columnId,
          payload: {
            column: {
              configuration: {
                width: newSize
              }
            }
          }
        });
      }, 500),
    [handleUpdateColumn]
  );

  const onColumnResize = useCallback(
    (column: GridColumn, newSize: number, colIndex: number, newSizeWithGrow: number) => {
      const columnId = column?.id;
      // if (currentColumns[colIndex]?.is_default) {
      //   console.warn('Cannot move default columns');
      //   return;
      // }
      debouncedUpdateColumn(columnId, newSize);
      setCurrentColumns((prevColumns) => {
        const updatedColumns = [...prevColumns];
        updatedColumns[colIndex] = { ...column, width: newSize }; // Update the column width
        return updatedColumns;
      });
    },
    [debouncedUpdateColumn]
  );

  const handleSelectRow = (d: GridSelection) => {
    const rowIds: number[] = d.rows
      .toArray()
      .map((rowIndex) => data[rowIndex]?.row_id)
      .filter((id): id is number => id !== undefined);
    setSelectedRows(rowIds);
    setSelection(d);
  };

  const handleUpdateName = (payload: any) => {
    handleUpdateColumn({
      columnId: columnID!,
      payload: {
        column: {
          name: payload.column_name
        }
      }
    });
  };

  const handleHideColumn = () => {
    handleUpdateColumn({
      columnId: columnID!,
      payload: {
        column: {
          hidden: true
        }
      }
    });
  };

  if (!worksheetData?.columns?.length) {
    return <EmptyStateView />;
  }

  return (
    <>
      <Box id='portal' maxHeight='100%'>
        <DataEditor
          className='worksheet_height'
          width='100%'
          smoothScrollX
          smoothScrollY
          rowMarkers={{
            kind: 'both',
            checkboxStyle: 'square'
          }}
          rowSelect='multi'
          rowSelectionMode='multi'
          onGridSelectionChange={handleSelectRow}
          gridSelection={selection}
          getCellContent={getCellContent}
          onHeaderMenuClick={onHeaderMenuClick}
          columns={currentColumns}
          headerIcons={customHeaderIcons}
          freezeColumns={handleFreezeColumnChange}
          customRenderers={[workflowButtonRenderer]}
          rows={worksheetData?.rows?.length!}
          onRowAppended={onRowAppended}
          onRowMoved={onRowMoved}
          onCellEdited={onCellEdited}
          trailingRowOptions={{
            hint: 'Add Row',
            sticky: true
          }}
          rowHeight={(index: number) => {
            const totalRows = worksheetData?.rows?.length || 0;
            const isTrailingRow = index === totalRows;

            return isTrailingRow ? 50 : selectedHeight;
          }}
          onColumnResize={onColumnResize}
          onColumnMoved={onColMoved}
          onColumnProposeMove={onColProposeMove}
          columnSelectionBlending='mixed'
          rangeSelectionBlending='mixed'
        />
        {isOpen &&
          renderLayer(
            <Box {...layerProps} className='menu'>
              <Paper sx={{ width: 220, maxWidth: '100%', mt: 1 }}>
                <Box px={2}>
                  <Box component='form' onSubmit={handleSubmit(handleUpdateName)}>
                    <Input
                      name='column_name'
                      label='Name'
                      defaultValue={columnName ?? ''}
                      control={control}
                      sx={{ marginTop: 1 }}
                      size='small'
                    />
                  </Box>
                </Box>
                <MenuList>
                  <Divider />
                  <MenuItem>
                    <ListItemIcon>
                      <TextIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography fontSize={14}>Text</Typography>
                    </ListItemText>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => handleHideColumn()}>
                    <ListItemIcon>
                      <HideColumn />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography fontSize={14}>Hide Column</Typography>
                    </ListItemText>
                  </MenuItem>
                  <MenuItem onClick={() => handleDeleteColumn(String(columnID))}>
                    <ListItemIcon>
                      <RiDeleteBin6Line color='red' size={14} />
                    </ListItemIcon>
                    <ListItemText sx={{ color: 'red' }}>
                      <Typography fontSize={14}>Delete</Typography>
                    </ListItemText>
                  </MenuItem>
                </MenuList>
              </Paper>
            </Box>
          )}
      </Box>
      <Box position='fixed' bottom='0' bgcolor='white' p={2} minWidth='92%'>
        <Box display='flex' justifyContent='space-between' alignItems='center' gap='10px'>
          <Box bgcolor='#F8F9FA'>
            <Button
              sx={{ width: '130px' }}
              onClick={() => {
                if (selectedRows.length > 0) {
                  handleDeleteRow({ ids: selectedRows });
                } else {
                  onRowAppended();
                }
                setSelectedRows([]);
                setSelection({ columns: CompactSelection.empty(), rows: CompactSelection.empty() });
              }}
            >
              {selectedRows.length > 0 ? 'Delete Row' : '+ Add Row'}
            </Button>
          </Box>
          <Box display='flex' gap='10px'>
            <Typography color='text.secondary' fontSize='14px'>
              {data.length || 0} Rows{' '}
            </Typography>
            <Typography color='text.secondary' fontSize='14px'>
              {columnsData?.data.length || 0}/{columnsData?.data.length} Columns{' '}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
}
